/**
 * Format a price in cents to a display string
 * @param {number} cents - Price in cents
 * @returns {string} Formatted price like "$500.00"
 */
export const formatPrice = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

/**
 * Format a Firestore timestamp or Date to a relative time string
 * @param {import('firebase/firestore').Timestamp | Date} timestamp
 * @returns {string} e.g. "2 days ago"
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';

  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  return date.toLocaleDateString();
};

/**
 * Format a Firestore timestamp to a full date string
 */
export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/**
 * Research type labels and colors
 */
export const RESEARCH_TYPES = {
  attribution: { label: 'Attribution', color: 'purple' },
  provenance: { label: 'Provenance', color: 'blue' },
  identification: { label: 'Identification', color: 'teal' },
  technical: { label: 'Technical Analysis', color: 'cyan' },
  heraldry: { label: 'Heraldry', color: 'orange' },
  paleography: { label: 'Paleography', color: 'pink' },
  archival: { label: 'Archival Research', color: 'yellow' },
  other: { label: 'Other', color: 'gray' },
};
